import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Package,
  MapPin,
  X,
  RefreshCw,
  Layers,
  TrendingDown,
  AlertTriangle,
  Check,
  Barcode,
  Printer,
  Copy,
  CheckCheck,
  Minus as MinusIcon,
  Upload,
  Download,
  FileSpreadsheet,
  Image as ImageIcon,
  ChevronRight,
  Eye,
  AlertCircle,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  TableProperties,
  Save,
  Percent,
} from "lucide-react";
import { toast } from "sonner";

const SpringModal = ({ children, onClose, maxW = "max-w-2xl" }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.93, y: 16 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.93, y: 16 }}
      transition={{ type: "spring", stiffness: 340, damping: 30 }}
      className={`bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full ${maxW} overflow-hidden max-h-[90vh] flex flex-col`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </motion.div>
  </motion.div>
);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = { W: 48, H: 30, GAP: 2, COLS: 3 };

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest block">
      {label}
      {required && <span className="text-rose-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const DarkInput = (props) => (
  <input
    {...props}
    className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 placeholder-stone-600 transition-all"
  />
);

// ── JsBarcode CDN loader (singleton) ─────────────────────────
let _jbPromise = null;
const loadJsBarcode = () => {
  if (_jbPromise) return _jbPromise;
  _jbPromise = new Promise((resolve, reject) => {
    if (window.JsBarcode) {
      resolve(window.JsBarcode);
      return;
    }
    const s = document.createElement("script");
    s.src =
      "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";
    s.onload = () => resolve(window.JsBarcode);
    s.onerror = () => {
      _jbPromise = null;
      reject(new Error("JsBarcode load failed"));
    };
    document.head.appendChild(s);
  });
  return _jbPromise;
};

const renderBarcode = async (svgEl, code, opts = {}) => {
  const jb = await loadJsBarcode();
  jb(svgEl, code, {
    format: "CODE128",
    lineColor: "#000",
    background: "#fff",
    width: 2.5,
    height: 70,
    displayValue: true,
    fontSize: 13,
    margin: 8,
    ...opts,
  });
};

const useBarcodeRef = (code, opts) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!code?.trim() || !ref.current) return;
    renderBarcode(ref.current, code.trim(), opts).catch(() => {});
  }, [code]); // eslint-disable-line
  return ref;
};

const calculateDiscountedPrice = (basePrice, discount, discountType) => {
  if (!discount || discount <= 0) return basePrice;
  if (discountType === "percentage") {
    return basePrice - (basePrice * discount) / 100;
  }
  return basePrice - discount; // flat
};

const getPrintMode = () => localStorage.getItem("mrb_print_mode") || "laser";
const setPrintMode = (m) => localStorage.setItem("mrb_print_mode", m);

const printLabelsLaser = async (labels) => {
  const jb = await loadJsBarcode();
  const makeLabelHTML = (l) => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    try {
      jb(svg, l.sku, {
        format: "CODE128",
        lineColor: "#000",
        background: "#fff",
        width: 1.6,
        height: 36,
        displayValue: true,
        fontSize: 8,
        margin: 2,
      });
    } catch {
      return "";
    }
    const dataUrl =
      "data:image/svg+xml;base64," +
      btoa(
        unescape(
          encodeURIComponent(new XMLSerializer().serializeToString(svg)),
        ),
      );
    return `<div class="label"><div class="shop">MRB Home Decor Mall</div><div class="name">${l.name.substring(0, 28)}</div><img src="${dataUrl}" class="barcode-img" /><div class="price">Rs.${Number(l.price || 0).toLocaleString("en-IN")}</div></div>`;
  };
  const LABELS_PER_PAGE = 48;
  const pages = [];
  for (let i = 0; i < labels.length; i += LABELS_PER_PAGE) {
    const chunkHTML = labels
      .slice(i, i + LABELS_PER_PAGE)
      .map(makeLabelHTML)
      .join("");
    pages.push(`<div class="sheet">${chunkHTML}</div>`);
  }
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Labels</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;background:#fff;width:210mm;}
.sheet{width:210mm;height:297mm;padding:5.5mm 6mm;display:grid;grid-template-columns:repeat(4,48mm);grid-template-rows:repeat(12,22mm);column-gap:2mm;row-gap:2mm;page-break-after:always;}
.sheet:last-child{page-break-after:auto;}.label{width:48mm;height:22mm;padding:0.8mm 1.2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;}
.shop{font-size:5px;color:#999;text-transform:uppercase;}.name{font-size:6.5px;font-weight:bold;color:#111;line-height:1.1;text-align:center;max-height:5mm;overflow:hidden;}
.barcode-img{width:44mm;height:9mm;object-fit:contain;display:block;}.price{font-size:8px;font-weight:900;color:#111;margin-top:.3mm;}
@media print{@page{size:A4 portrait;margin:0;}body{margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
</head><body>${pages.join("\n")}
<script>window.onload=function(){const imgs=document.querySelectorAll('img');let n=0;if(!imgs.length){window.print();return;}imgs.forEach(i=>{const done=()=>{if(++n===imgs.length)window.print();};i.complete?done():i.onload=i.onerror=done;});};window.onafterprint=function(){window.close();};<\/script></body></html>`;
  const w = window.open("", "_blank", "width=900,height=700");
  w.document.write(html);
  w.document.close();
};

const printLabelsThermal = async (labels, token) => {
  const DOT = 8;
  const blocks = labels.map((l) => {
    const name = (l.name || "").substring(0, 24).replace(/"/g, "'");
    const sku = (l.sku || "").replace(/"/g, "'");
    const price = `Rs.${Number(l.price || 0).toFixed(0)}`;
    return [
      "CLS",
      `TEXT 4,4,"1",0,1,1,"MRB Home Decor Mall"`,
      `TEXT 4,20,"2",0,1,1,"${name}"`,
      `BARCODE 4,44,"128",64,1,0,2,3,"${sku}"`,
      `TEXT 4,114,"1",0,1,1,"${sku}"`,
      `TEXT 4,130,"3",0,1,1,"${price}"`,
      "PRINT 1",
      "",
    ].join("\r\n");
  });
  const fullTSPL = [
    `SIZE ${LABEL.W} mm,${LABEL.H} mm`,
    `GAP ${LABEL.GAP} mm,0 mm`,
    "DIRECTION 1",
    "REFERENCE 0,0",
    "OFFSET 0 mm",
    "",
    ...blocks,
  ].join("\r\n");
  const res = await fetch(`${API_URL}/print-thermal`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ commands: fullTSPL }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Print failed (${res.status})`);
  }
  return res.json();
};

const printLabels = (labels, token) =>
  getPrintMode() === "thermal"
    ? printLabelsThermal(labels, token)
    : printLabelsLaser(labels);

const PrintModeToggle = ({ value, onChange }) => (
  <div className="flex items-center gap-1 bg-white/8 border border-white/10 rounded-xl p-1">
    {[
      { id: "laser", label: "🖨️ Laser" },
      { id: "thermal", label: "🏷️ Thermal" },
    ].map((opt) => (
      <button
        key={opt.id}
        onClick={() => onChange(opt.id)}
        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${value === opt.id ? "bg-amber-500 text-stone-900 shadow" : "text-stone-400 hover:text-white"}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const BarcodeDisplay = ({ code, name, price }) => {
  const svgRef = useRef(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!code?.trim() || !svgRef.current) {
      setError(false);
      return;
    }
    setError(false);
    loadJsBarcode()
      .then((jb) => {
        try {
          while (svgRef.current.firstChild)
            svgRef.current.removeChild(svgRef.current.firstChild);
          svgRef.current.removeAttribute("style");
          jb(svgRef.current, code.trim(), {
            format: "CODE128",
            lineColor: "#000",
            background: "#fff",
            width: 2.5,
            height: 70,
            displayValue: true,
            fontSize: 13,
            margin: 8,
          });
        } catch {
          setError(true);
        }
      })
      .catch(() => setError(true));
  }, [code]);
  if (!code?.trim())
    return (
      <div className="bg-white rounded-xl p-4 min-h-[90px] flex items-center justify-center">
        <p className="text-stone-300 text-xs text-center">
          Enter or generate a barcode to see preview
        </p>
      </div>
    );
  return (
    <div className="bg-white rounded-xl p-3 text-center">
      {error ? (
        <p className="text-rose-400 text-xs py-6">
          Invalid barcode value — use letters and numbers only
        </p>
      ) : (
        <>
          <p className="text-[8px] text-stone-400 uppercase tracking-widest mb-1">
            MRB Home Decor Mall
          </p>
          <p className="font-bold text-stone-800 text-xs mb-2 truncate px-2">
            {name || "Product Name"}
          </p>
          <svg ref={svgRef} className="mx-auto max-w-full" />
          {price && (
            <p className="font-black text-stone-900 text-lg mt-1">₹{price}</p>
          )}
        </>
      )}
    </div>
  );
};

const ImageUploadField = ({ value, onChange, token }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      onChange(e.target.result);
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(`${API_URL}/upload-image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (res.ok) {
          const d = await res.json();
          if (d.url) onChange(d.url);
        }
      } catch {
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-3">
      {value ? (
        <div
          className="relative group rounded-2xl overflow-hidden border border-white/10 bg-white/5"
          style={{ height: 180 }}
        >
          <img
            src={value}
            alt="Product"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-all duration-200 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100">
            <label className="flex flex-col items-center gap-1.5 cursor-pointer">
              <div className="w-10 h-10 rounded-xl bg-white/15 hover:bg-amber-500/80 border border-white/20 flex items-center justify-center transition-all">
                <Upload size={16} className="text-white" />
              </div>
              <span className="text-[10px] text-white font-bold">Change</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) =>
                  e.target.files?.[0] && handleFile(e.target.files[0])
                }
              />
            </label>
            <button
              onClick={() =>
                value.startsWith("data:")
                  ? (() => {
                      const w = window.open();
                      w.document.write(
                        `<img src="${value}" style="max-width:100%;display:block;margin:auto;" />`,
                      );
                    })()
                  : window.open(value, "_blank", "noopener,noreferrer")
              }
              className="flex flex-col items-center gap-1.5"
            >
              <div className="w-10 h-10 rounded-xl bg-white/15 hover:bg-blue-500/80 border border-white/20 flex items-center justify-center transition-all">
                <Eye size={16} className="text-white" />
              </div>
              <span className="text-[10px] text-white font-bold">View</span>
            </button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw size={20} className="text-amber-400 animate-spin" />
                <span className="text-xs text-white font-semibold">
                  Uploading…
                </span>
              </div>
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-[9px] text-stone-300 truncate">
              {value.startsWith("data:") ? "Local file" : value}
            </p>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            e.dataTransfer.files[0] && handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
          className={`relative flex flex-col items-center justify-center gap-2 h-36 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${dragging ? "border-amber-400 bg-amber-500/10" : "border-white/15 bg-white/5 hover:border-white/30 hover:bg-white/8"}`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) =>
              e.target.files?.[0] && handleFile(e.target.files[0])
            }
          />
          {uploading ? (
            <>
              <RefreshCw size={22} className="text-amber-400 animate-spin" />
              <p className="text-[11px] text-stone-400">Uploading…</p>
            </>
          ) : (
            <>
              <div
                className={`p-3 rounded-xl transition-colors ${dragging ? "bg-amber-500/20" : "bg-white/8"}`}
              >
                <ImageIcon
                  size={22}
                  className={dragging ? "text-amber-400" : "text-stone-500"}
                />
              </div>
              <div className="text-center">
                <p className="text-[12px] text-stone-300 font-semibold">
                  {dragging ? "Drop image here" : "Click or drag & drop"}
                </p>
                <p className="text-[10px] text-stone-600 mt-0.5">
                  PNG, JPG, WEBP · max 5 MB
                </p>
              </div>
            </>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="url"
          value={value?.startsWith("data:") ? "" : value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL…"
          className="flex-1 h-9 bg-white/5 border border-white/10 rounded-xl px-3 text-white text-xs outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600 transition-all"
        />
        {value && !value.startsWith("data:") && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(value);
              toast.success("URL copied!");
            }}
            className="h-9 px-3 rounded-xl bg-white/8 hover:bg-white/15 border border-white/10 text-stone-400 hover:text-white transition-all flex-shrink-0"
          >
            <Copy size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Product Modal ─────────────────────────────────────────────
const ProductModal = ({ editingProduct, onClose, onSave, token }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: editingProduct?.name || "",
    sku: editingProduct?.sku || editingProduct?.barcode || "",
    category: editingProduct?.category || "",
    purchase_price: editingProduct?.purchase_price?.toString() || "",
    selling_price: editingProduct?.selling_price?.toString() || "",
    stock_quantity: editingProduct?.stock_quantity?.toString() || "",
    showroom_quantity:
      editingProduct?.showroom_quantity?.toString() ??
      editingProduct?.stock_quantity?.toString() ??
      "",
    warehouse_quantity: editingProduct?.warehouse_quantity?.toString() ?? "0",
    supplier: editingProduct?.supplier || "",
    discount:
      editingProduct?.discount != null
        ? editingProduct.discount.toString()
        : "0",
    discount_type: editingProduct?.discount_type || "percentage",
    image_url: editingProduct?.image_url || "",
    low_stock_threshold: editingProduct?.low_stock_threshold?.toString() || "1",
    // ── NEW ──
    hsn: editingProduct?.hsn || "",
    gst_rate:
      editingProduct?.gst_rate != null
        ? editingProduct.gst_rate.toString()
        : "",
    product_source: editingProduct?.product_source || "showroom",
  });
  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const generateBarcode = () => {
    set(
      "sku",
      `${Date.now().toString().slice(-6)}${Math.floor(Math.random() * 900 + 100)}`,
    );
  };

  const handlePrintSingle = async () => {
    if (!form.sku.trim() || !form.name.trim()) {
      toast.error("Enter product name and barcode first");
      return;
    }
    try {
      await printLabels(
        [{ name: form.name, sku: form.sku, price: form.selling_price }],
        token,
      );
      if (getPrintMode() === "thermal")
        toast.success("Sent to thermal printer!");
    } catch (err) {
      toast.error(err.message || "Print failed");
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.sku || !form.category) {
      toast.error("Name, Barcode and Category are required");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        purchase_price: parseFloat(form.purchase_price) || 0,
        selling_price: parseFloat(form.selling_price) || 0,
        showroom_quantity: parseInt(form.showroom_quantity) || 0,
        warehouse_quantity: parseInt(form.warehouse_quantity) || 0,
        stock_quantity:
          (parseInt(form.showroom_quantity) || 0) +
          (parseInt(form.warehouse_quantity) || 0),
        low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
        hsn: form.hsn.trim(),
        // Send gst_rate only when a value is entered; empty string → null (backend ignores null)
        gst_rate: form.gst_rate !== "" ? parseFloat(form.gst_rate) : null,
        product_source: form.product_source || "showroom",
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <SpringModal onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl">
            <Package size={17} className="text-amber-400" />
          </div>
          <h2 className="text-base font-bold text-white">
            {editingProduct
              ? t("inventory.editProduct")
              : t("inventory.addProduct")}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {/* Barcode section */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Barcode size={14} className="text-amber-400" />
              <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                Barcode / SKU *
              </span>
            </div>
            <button
              onClick={generateBarcode}
              className="flex items-center gap-1.5 text-[11px] font-black bg-amber-500 hover:bg-amber-400 text-stone-900 px-3 py-1.5 rounded-lg transition-all"
            >
              <RefreshCw size={11} /> Generate New
            </button>
          </div>
          <div className="flex gap-2 mb-3">
            <DarkInput
              value={form.sku}
              onChange={(e) => set("sku", e.target.value)}
              placeholder="Enter barcode or click Generate"
              style={{
                fontFamily: "monospace",
                letterSpacing: "0.12em",
                fontSize: "1rem",
              }}
            />
            {form.sku && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(form.sku);
                  toast.success("Copied!");
                }}
                className="px-3 rounded-xl bg-white/10 hover:bg-white/15 text-stone-300 hover:text-white transition-colors flex-shrink-0"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
          <BarcodeDisplay
            code={form.sku}
            name={form.name}
            price={form.selling_price}
          />
          {form.sku && form.name && (
            <button
              onClick={handlePrintSingle}
              className="mt-3 w-full flex items-center justify-center gap-2 h-9 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition-all"
            >
              <Printer size={13} /> Print This Label
            </button>
          )}
          <p className="text-[10px] text-stone-600 text-center mt-2">
            ✓ Scannable by Fingers and all USB barcode scanners
          </p>
        </div>

        {/* Product details */}
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("inventory.productName")} required>
            <DarkInput
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Product name"
            />
          </Field>
          <Field label={t("inventory.category")} required>
            <DarkInput
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              placeholder="e.g. Vases"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("inventory.supplier")}>
            <DarkInput
              value={form.supplier}
              onChange={(e) => set("supplier", e.target.value)}
              placeholder="Supplier name"
            />
          </Field>
          <Field label={t("inventory.lowStockThreshold")}>
            <DarkInput
              type="number"
              value={form.low_stock_threshold}
              onChange={(e) => set("low_stock_threshold", e.target.value)}
              placeholder="1"
            />
          </Field>
        </div>

        {/* ── NEW: HSN + GST row ── */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="HSN Code">
            <DarkInput
              value={form.hsn}
              onChange={(e) => set("hsn", e.target.value)}
              placeholder="e.g. 69120000"
              style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}
            />
          </Field>
          <Field label="GST Rate (%)">
            <div className="relative">
              <DarkInput
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={form.gst_rate}
                onChange={(e) => set("gst_rate", e.target.value)}
                placeholder="0 / 5 / 12 / 18 / 28"
                style={{ paddingRight: "2.5rem" }}
              />
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-500 text-xs font-bold pointer-events-none">
                %
              </span>
            </div>
          </Field>
        </div>
        {/* ── Product Source ── */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers size={14} className="text-purple-400" />
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
              Product Source
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { val: "showroom", label: "🏪 Showroom", color: "amber" },
              { val: "warehouse", label: "🏭 Warehouse", color: "blue" },
              { val: "both", label: "🔄 Both", color: "emerald" },
            ].map(({ val, label, color }) => (
              <button
                key={val}
                type="button"
                onClick={() => set("product_source", val)}
                className={`h-10 rounded-xl font-bold text-xs border transition-all ${
                  form.product_source === val
                    ? `bg-${color}-500/20 border-${color}-500/60 text-${color}-300`
                    : "bg-white/5 border-white/10 text-stone-400 hover:border-white/20"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Percent size={14} className="text-blue-400" />
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
              Product Discount
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <Field label="Discount Type">
              <select
                value={form.discount_type}
                onChange={(e) => set("discount_type", e.target.value)}
                className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm outline-none focus:ring-2 focus:ring-amber-500/40"
              >
                <option
                  style={{ color: "black" }}
                  className="text-black"
                  value="percentage"
                >
                  Percentage (%)
                </option>
                <option
                  style={{ color: "black" }}
                  className="text-black"
                  value="flat"
                >
                  Flat Amount (₹)
                </option>
              </select>
            </Field>

            <Field label="Discount Value">
              <div className="relative">
                {form.discount_type === "flat" && (
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-400 text-sm font-bold">
                    ₹
                  </span>
                )}
                <DarkInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discount}
                  onChange={(e) => set("discount", e.target.value)}
                  placeholder="0"
                />
                {form.discount_type === "percentage" && (
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-500 text-xs font-bold">
                    %
                  </span>
                )}
              </div>
            </Field>
          </div>

          {/* Live price preview */}
          {parseFloat(form.selling_price) > 0 &&
            parseFloat(form.discount) > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-stone-400">Original Price:</span>
                  <span className="text-white font-mono">
                    ₹{parseFloat(form.selling_price).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-stone-400">Discount:</span>
                  <span className="text-blue-400 font-mono">
                    -
                    {form.discount_type === "percentage"
                      ? `${form.discount}%`
                      : `₹${parseFloat(form.discount).toFixed(2)}`}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-1.5 mt-1 flex items-center justify-between">
                  <span className="text-stone-300 font-bold text-sm">
                    Final Price:
                  </span>
                  <span className="text-emerald-400 font-black text-lg font-mono">
                    ₹
                    {calculateDiscountedPrice(
                      parseFloat(form.selling_price),
                      parseFloat(form.discount),
                      form.discount_type,
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: t("inventory.purchasePrice"), key: "purchase_price" },
            { label: t("inventory.sellingPrice"), key: "selling_price" },
          ].map(({ label, key }) => (
            <Field key={key} label={label} required>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-400 text-sm font-bold">
                  ₹
                </span>
                <DarkInput
                  type="number"
                  step="0.01"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder="0.00"
                  style={{ paddingLeft: "2rem" }}
                />
              </div>
            </Field>
          ))}
          {/* ── Purchase / Selling Price ── */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: t("inventory.purchasePrice"), key: "purchase_price" },
            ].map(({ label, key }) => (
              <Field key={key} label={label} required>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-400 text-sm font-bold">
                    ₹
                  </span>
                  <DarkInput
                    type="number"
                    step="0.01"
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    placeholder="0.00"
                    style={{ paddingLeft: "2rem" }}
                  />
                </div>
              </Field>
            ))}
          </div>

          {/* ── Split Stock Section ── */}
          <div className="md:col-span-3">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Layers size={14} className="text-emerald-400" />
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                  Stock Quantities
                </span>
              </div>

              {form.product_source === "both" ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      {
                        label: "🏪 Showroom Stock",
                        key: "showroom_quantity",
                        color: "amber",
                      },
                      {
                        label: "🏭 Warehouse Stock",
                        key: "warehouse_quantity",
                        color: "blue",
                      },
                    ].map(({ label, key }) => (
                      <Field key={key} label={label} required>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              set(
                                key,
                                Math.max(0, parseInt(form[key] || 0) - 1),
                              )
                            }
                            className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                          >
                            <MinusIcon size={13} />
                          </button>
                          <DarkInput
                            type="number"
                            min="0"
                            value={form[key]}
                            onChange={(e) => set(key, e.target.value)}
                            placeholder="0"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              set(key, parseInt(form[key] || 0) + 1)
                            }
                            className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                      </Field>
                    ))}
                  </div>
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2.5">
                    <span className="text-xs text-stone-400 font-semibold">
                      Total Stock
                    </span>
                    <span className="font-mono font-black text-emerald-400 text-xl">
                      {(parseInt(form.showroom_quantity) || 0) +
                        (parseInt(form.warehouse_quantity) || 0)}
                      <span className="text-xs text-stone-500 font-normal ml-1">
                        units
                      </span>
                    </span>
                  </div>
                </div>
              ) : form.product_source === "warehouse" ? (
                <Field label="🏭 Warehouse Stock" required>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "warehouse_quantity",
                          Math.max(
                            0,
                            parseInt(form.warehouse_quantity || 0) - 1,
                          ),
                        )
                      }
                      className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <MinusIcon size={13} />
                    </button>
                    <DarkInput
                      type="number"
                      min="0"
                      value={form.warehouse_quantity}
                      onChange={(e) => {
                        set("warehouse_quantity", e.target.value);
                        set("showroom_quantity", "0");
                      }}
                      placeholder="0"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "warehouse_quantity",
                          parseInt(form.warehouse_quantity || 0) + 1,
                        )
                      }
                      className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                </Field>
              ) : (
                /* default: showroom */
                <Field label="🏪 Showroom Stock" required>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "showroom_quantity",
                          Math.max(
                            0,
                            parseInt(form.showroom_quantity || 0) - 1,
                          ),
                        )
                      }
                      className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <MinusIcon size={13} />
                    </button>
                    <DarkInput
                      type="number"
                      min="0"
                      value={form.showroom_quantity}
                      onChange={(e) => {
                        set("showroom_quantity", e.target.value);
                        set("warehouse_quantity", "0");
                      }}
                      placeholder="0"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "showroom_quantity",
                          parseInt(form.showroom_quantity || 0) + 1,
                        )
                      }
                      className="w-9 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                </Field>
              )}
            </div>
          </div>
        </div>

        <Field label="Product Image">
          <ImageUploadField
            value={`${process.env.REACT_APP_BACKEND_URL}${form.image_url}`}
            onChange={(v) => set("image_url", v)}
            token={token}
          />
        </Field>
        {form.image_url && (
          <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-white/10">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${form.image_url}`}
              alt="Preview"
              className="w-12 h-12 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <p className="text-xs text-stone-400">Image preview</p>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-white/10 flex gap-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white hover:border-white/20 text-sm font-semibold transition-all"
        >
          {t("common.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {saving ? (
            <>
              <RefreshCw size={13} className="animate-spin" />
              {t("common.loading")}
            </>
          ) : editingProduct ? (
            t("inventory.update")
          ) : (
            t("inventory.add")
          )}
        </button>
      </div>
    </SpringModal>
  );
};

// ── Rack Modal ────────────────────────────────────────────────
const RackModal = ({ product, racks, onClose, token }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ rack_id: "", quantity: "" });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/products/${product.id}/rack-assignments`,
        { headers },
      );
      setAssignments(r.data);
    } catch {
      toast.error("Failed to load rack assignments");
    } finally {
      setLoading(false);
    }
  }, [product.id, headers]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleAssign = async () => {
    if (!form.rack_id || !form.quantity) {
      toast.error("Select a rack and enter quantity");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/rack-assignments`,
        {
          product_id: product.id,
          rack_id: form.rack_id,
          quantity: parseInt(form.quantity),
        },
        { headers },
      );
      toast.success("Rack assignment created");
      setForm({ rack_id: "", quantity: "" });
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign rack");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    try {
      await axios.delete(`${API_URL}/rack-assignments/${id}`, { headers });
      toast.success("Assignment removed");
      fetchAssignments();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const totalAssigned = assignments.reduce((s, a) => s + a.quantity, 0);
  return (
    <SpringModal onClose={onClose} maxW="max-w-xl">
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/15 rounded-xl">
            <MapPin size={16} className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">
              {t("inventory.rackAssignments")}
            </h2>
            <p className="text-[10px] text-stone-500">{product.name}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm text-stone-400 font-semibold">
            {t("inventory.totalAssigned")}
          </span>
          <span className="font-mono font-black text-amber-400 text-lg">
            {totalAssigned} / {product.stock_quantity}{" "}
            <span className="text-xs text-stone-500">
              {t("inventory.units")}
            </span>
          </span>
        </div>
        <div>
          <p className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-3">
            {t("inventory.currentRackLocations")}
          </p>
          {loading ? (
            <div className="flex justify-center py-6">
              <RefreshCw size={18} className="animate-spin text-stone-500" />
            </div>
          ) : assignments.length === 0 ? (
            <p className="text-center text-stone-500 text-xs py-6">
              {t("inventory.noAssignments")}
            </p>
          ) : (
            <div className="space-y-2">
              {assignments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between bg-white/5 border border-white/8 rounded-xl px-4 py-3 group"
                >
                  <div>
                    <p className="text-sm font-bold text-white">
                      {a.rack_code} — {a.location_name}
                    </p>
                    <p className="text-[10px] text-stone-500">
                      {t("inventory.assignedBy")} {a.assigned_by_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-black text-amber-400">
                      {a.quantity}{" "}
                      <span className="text-xs text-stone-500">
                        {t("inventory.units")}
                      </span>
                    </span>
                    <button
                      onClick={() => handleDelete(a.id)}
                      className="p-1.5 rounded-lg text-stone-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-white/10 pt-4">
          <p className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-3">
            {t("inventory.assignToNewRack")}
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1.5 block">
                {t("inventory.selectRack")}
              </label>
              <select
                value={form.rack_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rack_id: e.target.value }))
                }
                className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-3 text-stone-300 text-sm outline-none focus:ring-2 focus:ring-amber-500/40"
              >
                <option value="" className="bg-[#1c1c2e]">
                  {t("inventory.chooseRack")}
                </option>
                {racks.map((r) => (
                  <option key={r.id} value={r.id} className="bg-[#1c1c2e]">
                    {r.code} — {r.name} ({r.location_name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1.5 block">
                {t("inventory.quantity")}
              </label>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quantity: e.target.value }))
                }
                placeholder={t("inventory.enterQuantity")}
                className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
              />
            </div>
          </div>
          <button
            onClick={handleAssign}
            disabled={saving}
            className="w-full h-11 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <MapPin size={13} />
            )}
            {t("inventory.assignToRack")}
          </button>
        </div>
      </div>
    </SpringModal>
  );
};

// ── Barcode Modal ─────────────────────────────────────────────
const BarcodeModal = ({ product, token, onClose }) => {
  const barcodeCode = product.barcode || product.sku;
  const svgRef = useBarcodeRef(barcodeCode, {
    width: 2.5,
    height: 65,
    fontSize: 13,
    margin: 8,
  });
  const [copies, setCopies] = useState(1);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(barcodeCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const handlePrint = async () => {
    const labels = Array.from({ length: copies }, () => ({
      name: product.name,
      sku: barcodeCode,
      price: product.selling_price,
    }));
    try {
      await printLabels(labels, token);
      if (getPrintMode() === "thermal") toast.success("Sent to printer!");
    } catch (err) {
      toast.error(err.message || "Print failed");
    }
  };

  return (
    <SpringModal onClose={onClose} maxW="max-w-sm">
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl border border-amber-500/20">
            <Barcode size={16} className="text-amber-400" />
          </div>
          <h2 className="font-black text-white text-sm">Barcode Label</h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="px-6 py-5">
        <div className="bg-white rounded-2xl p-4 text-center shadow-lg border border-stone-200">
          <p className="text-[8px] text-stone-400 uppercase tracking-widest mb-1">
            MRB Home Decor Mall
          </p>
          <p className="font-black text-stone-800 text-sm leading-tight mb-3 line-clamp-2">
            {product.name}
          </p>
          <svg ref={svgRef} className="mx-auto max-w-full" />
          <p className="font-mono text-[9px] text-stone-400 mt-1 tracking-wider">
            {barcodeCode}
          </p>
          <p className="font-black text-stone-900 text-xl mt-2">
            ₹
            {Number(product.selling_price).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
        <div className="mt-4 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
          <span className="text-xs font-mono text-stone-300 flex-1 truncate">
            {barcodeCode}
          </span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-[11px] font-bold text-amber-400 hover:text-amber-300 transition-colors flex-shrink-0"
          >
            {copied ? (
              <>
                <CheckCheck size={13} />
                Copied!
              </>
            ) : (
              <>
                <Copy size={13} />
                Copy
              </>
            )}
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
          <span className="text-xs text-stone-400 font-semibold">
            Print copies
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCopies((c) => Math.max(1, c - 1))}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <MinusIcon size={12} />
            </button>
            <span className="text-white font-black text-sm w-6 text-center">
              {copies}
            </span>
            <button
              onClick={() => setCopies((c) => Math.min(50, c + 1))}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 border-t border-white/10 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
        >
          Close
        </button>
        <button
          onClick={handlePrint}
          className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm flex items-center justify-center gap-2 transition-all"
        >
          <Printer size={14} /> Print {copies > 1 ? `(${copies})` : ""}
        </button>
      </div>
    </SpringModal>
  );
};

// ── XLSX helpers ──────────────────────────────────────────────
const XLSX_COLS = [
  "name",
  "sku",
  "category",
  "purchase_price",
  "selling_price",
  "stock_quantity",
  "supplier",
  "low_stock_threshold",
  "hsn",
  "discount",
  "discount_type",
  "gst_rate",
  "image_url",
  "product_source",
];

const loadXLSX = () =>
  new Promise((resolve, reject) => {
    if (window.XLSX) {
      resolve(window.XLSX);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js";
    s.onload = () => resolve(window.XLSX);
    s.onerror = () => reject(new Error("Failed to load Excel library"));
    document.head.appendChild(s);
  });

const loadJSZip = () =>
  new Promise((resolve, reject) => {
    if (window.JSZip) {
      resolve(window.JSZip);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    s.onload = () => resolve(window.JSZip);
    s.onerror = reject;
    document.head.appendChild(s);
  });

const downloadXLSXTemplate = async () => {
  try {
    const XLSX = await loadXLSX();
    const ws_data = [
      XLSX_COLS,
      [
        "Ceramic Vase Blue",
        "CV001",
        "Vases",
        "200",
        "499",
        "10",
        "Artisan Co.",
        "5",
        "69120000",
        "18",
        "",
        "showroom",
      ],
      [
        "Wall Clock Teak",
        "WC002",
        "Clocks",
        "350",
        "799",
        "20",
        "TimeCo.",
        "3",
        "91051900",
        "18",
        "",
        "showroom",
      ],
      [
        "Ceramic Vase Blue",
        "CV001",
        "Vases",
        "200",
        "499",
        "10",
        "Artisan Co.",
        "5",
        "69120000",
        "18",
        "10",
        "percentage",
        "",
        "showroom",
      ],
      [
        "Wall Clock Teak",
        "WC002",
        "Clocks",
        "350",
        "799",
        "20",
        "TimeCo.",
        "3",
        "91051900",
        "18",
        "50",
        "flat",
        "",
        "showroom",
      ],
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    ws["!cols"] = [25, 14, 16, 14, 14, 14, 18, 20, 14, 10, 35, 13].map(
      (wch) => ({
        wch,
      }),
    );
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "mrb_products_template.xlsx");
  } catch (err) {
    toast.error(err.message);
  }
};

const parseXLSX = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const [XLSX, JSZip] = await Promise.all([loadXLSX(), loadJSZip()]);
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, {
          type: "array",
          cellStyles: true,
          cellDates: true,
        });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (raw.length < 2) {
          resolve({
            rows: [],
            errors: ["Excel file must have at least one data row"],
          });
          return;
        }
        const headers = raw[0].map((h) =>
          String(h).trim().toLowerCase().replace(/\s+/g, "_"),
        );
        const missing = ["name", "sku", "category", "selling_price"].filter(
          (r) => !headers.includes(r),
        );
        if (missing.length) {
          resolve({
            rows: [],
            errors: [`Missing columns: ${missing.join(", ")}`],
          });
          return;
        }

        let drawingRowMap = {};
        try {
          const zip = await JSZip.loadAsync(data);
          const mediaFiles = {};
          await Promise.all(
            Object.entries(zip.files)
              .filter(([path]) => path.startsWith("xl/media/"))
              .map(async ([path, entry]) => {
                const ext = path.split(".").pop().toLowerCase();
                const mimeMap = {
                  png: "image/png",
                  jpg: "image/jpeg",
                  jpeg: "image/jpeg",
                  gif: "image/gif",
                  webp: "image/webp",
                };
                if (mimeMap[ext])
                  mediaFiles[path] =
                    `data:${mimeMap[ext]};base64,${await entry.async("base64")}`;
              }),
          );
          const relsFile = zip.file("xl/drawings/_rels/drawing1.xml.rels");
          const rIdToMedia = {};
          if (relsFile) {
            const doc = new DOMParser().parseFromString(
              await relsFile.async("string"),
              "text/xml",
            );
            doc.querySelectorAll("Relationship").forEach((rel) => {
              const resolved =
                "xl/media/" + rel.getAttribute("Target").split("/").pop();
              if (mediaFiles[resolved])
                rIdToMedia[rel.getAttribute("Id")] = mediaFiles[resolved];
            });
          }
          const drawingFile = zip.file("xl/drawings/drawing1.xml");
          if (drawingFile) {
            const doc = new DOMParser().parseFromString(
              await drawingFile.async("string"),
              "text/xml",
            );
            [
              ...doc.querySelectorAll("twoCellAnchor"),
              ...doc.querySelectorAll("oneCellAnchor"),
            ].forEach((anchor) => {
              const fromRow = anchor.querySelector("from > row");
              const blip = anchor.querySelector("blip");
              if (!fromRow || !blip) return;
              const rId =
                blip.getAttribute("r:embed") ||
                blip.getAttributeNS(
                  "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
                  "embed",
                );
              const dataRowIdx = parseInt(fromRow.textContent, 10) - 1;
              if (dataRowIdx >= 0 && rId && rIdToMedia[rId])
                drawingRowMap[dataRowIdx] = rIdToMedia[rId];
            });
          }
        } catch (e) {
          console.warn("Image extraction failed:", e);
        }

        const rows = raw
          .slice(1)
          .map((cols, i) => {
            const row = { _line: i + 2, _errors: [] };
            headers.forEach((h, j) => {
              row[h] = String(cols[j] ?? "").trim();
            });
            if (!row.name) row._errors.push("Name required");
            if (!row.sku) row._errors.push("SKU/Barcode required");
            if (!row.category) row._errors.push("Category required");
            if (row.selling_price && isNaN(parseFloat(row.selling_price)))
              row._errors.push("Invalid selling price");
            if (drawingRowMap[i]) {
              row.image_url = drawingRowMap[i];
              row._hasEmbeddedImage = true;
            }
            return row;
          })
          .filter((r) =>
            ["name", "sku", "category", "selling_price", "stock_quantity"].some(
              (k) => r[k],
            ),
          );
        resolve({ rows, errors: [] });
      } catch (err) {
        resolve({
          rows: [],
          errors: [`Failed to parse Excel file: ${err.message}`],
        });
      }
    };
    reader.readAsArrayBuffer(file);
  });
};

// ── XLSX Import Modal ─────────────────────────────────────────
const XLSXImportModal = ({ onClose, onImport, token }) => {
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i) && !file.type.includes("spreadsheet")) {
      toast.error("Please upload an Excel file (.xlsx)");
      return;
    }
    setFileName(file.name);
    const { rows: parsed, errors } = await parseXLSX(file);
    setParseErrors(errors);
    setRows(parsed);
    if (!errors.length && parsed.length) setStep("preview");
  };

  const handleRowImage = (rowIdx, file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
              ...r,
              image_url: URL.createObjectURL(file),
              _pendingImageFile: file,
            }
          : r,
      ),
    );
  };

  const editCell = (rowIdx, col, val) =>
    setRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [col]: val } : r)),
    );
  const removeRow = (rowIdx) =>
    setRows((prev) => prev.filter((_, i) => i !== rowIdx));

  const handleImport = async () => {
    const valid = rows.filter((r) => !r._errors?.length);
    if (!valid.length) {
      toast.error("No valid rows to import");
      return;
    }
    setStep("importing");
    setProgress({ done: 0, total: valid.length });
    let success = 0,
      failed = 0;
    for (const row of valid) {
      let imageUrl = row.image_url || "";
      if (row._pendingImageFile instanceof File) {
        try {
          const form = new FormData();
          form.append("file", row._pendingImageFile);
          const res = await fetch(`${API_URL}/upload-image`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          if (res.ok) {
            const d = await res.json();
            if (d.url) imageUrl = d.url;
          }
          if (imageUrl.startsWith("blob:")) {
            imageUrl = await new Promise((resolve) => {
              const r = new FileReader();
              r.onload = (e) => resolve(e.target.result);
              r.readAsDataURL(row._pendingImageFile);
            });
          }
        } catch {
          imageUrl = await new Promise((resolve) => {
            const r = new FileReader();
            r.onload = (e) => resolve(e.target.result);
            r.readAsDataURL(row._pendingImageFile);
          }).catch(() => "");
        }
      }
      try {
        await axios.post(
          `${API_URL}/products`,
          {
            name: row.name,
            sku: row.sku,
            category: row.category,
            purchase_price: parseFloat(row.purchase_price) || 0,
            selling_price: parseFloat(row.selling_price) || 0,
            stock_quantity: parseInt(row.stock_quantity) || 0,
            supplier: row.supplier || "",
            low_stock_threshold: parseInt(row.low_stock_threshold) || 10,
            hsn: row.hsn || "",
            gst_rate: row.gst_rate ? parseFloat(row.gst_rate) : null,
            image_url: imageUrl,
            product_source: row.product_source || "showroom",
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        success++;
      } catch {
        failed++;
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    toast.success(
      `Imported ${success} product${success !== 1 ? "s" : ""}${failed ? ` (${failed} failed)` : ""}`,
    );
    onImport();
    onClose();
  };

  const validRows = rows.filter((r) => !r._errors?.length);
  const invalidRows = rows.filter((r) => r._errors?.length);
  const embeddedImageCount = rows.filter((r) => r._hasEmbeddedImage).length;

  const StepIndicator = () => (
    <div className="hidden sm:flex items-center gap-1 text-[11px]">
      {["Upload", "Preview & Edit", "Import"].map((s, i) => {
        const ids = ["upload", "preview", "importing"];
        const active = step === ids[i];
        const done =
          (step === "preview" && i === 0) || (step === "importing" && i < 2);
        return (
          <React.Fragment key={s}>
            <span
              className={`px-2.5 py-1 rounded-lg font-bold transition-all ${active ? "bg-amber-500 text-stone-900" : done ? "text-emerald-400" : "text-stone-600"}`}
            >
              {done ? "✓ " : ""}
              {s}
            </span>
            {i < 2 && <ChevronRight size={12} className="text-stone-700" />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <SpringModal onClose={onClose} maxW="max-w-5xl">
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/15 rounded-xl border border-emerald-500/20">
            <FileSpreadsheet size={17} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">
              Import Products via Excel
            </h2>
            <p className="text-[10px] text-stone-500">
              Upload an .xlsx file — images inserted into cells are
              automatically extracted
            </p>
          </div>
        </div>
        <StepIndicator />
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors ml-4"
        >
          <X size={16} />
        </button>
      </div>

      {step === "upload" && (
        <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col items-center justify-center gap-6">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFile(e.dataTransfer.files[0]);
            }}
            className="w-full max-w-lg border-2 border-dashed border-white/20 hover:border-emerald-500/50 rounded-2xl p-10 text-center cursor-pointer transition-all group"
          >
            <div className="p-4 bg-emerald-500/10 rounded-2xl inline-flex mb-4 group-hover:bg-emerald-500/20 transition-colors">
              <FileSpreadsheet size={28} className="text-emerald-400" />
            </div>
            <p className="text-white font-bold mb-1">
              Drop your Excel file here
            </p>
            <p className="text-stone-500 text-sm mb-4">or click to browse</p>
            <span className="text-[11px] bg-white/10 text-stone-400 px-3 py-1.5 rounded-full font-mono">
              .xlsx files only
            </span>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </div>
          {parseErrors.length > 0 && (
            <div className="w-full max-w-lg bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
              {parseErrors.map((e, i) => (
                <p key={i} className="text-rose-400 text-sm">
                  ⚠️ {e}
                </p>
              ))}
            </div>
          )}
          <div className="w-full max-w-lg bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-white font-bold text-sm mb-1">
                  📄 Download Excel Template
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {XLSX_COLS.map((c) => (
                    <span
                      key={c}
                      className="text-[10px] font-mono bg-white/10 text-stone-400 px-2 py-0.5 rounded"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <button
                onClick={downloadXLSXTemplate}
                className="flex-shrink-0 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
              >
                <Download size={13} /> Template
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "preview" && (
        <>
          <div className="px-6 py-3 border-b border-white/10 flex flex-wrap items-center gap-3 flex-shrink-0">
            <span className="text-xs text-stone-400">
              {rows.length} rows from{" "}
              <span className="text-white font-bold">{fileName}</span>
            </span>
            <span className="text-xs text-emerald-400 font-bold">
              ✓ {validRows.length} valid
            </span>
            {invalidRows.length > 0 && (
              <span className="text-xs text-rose-400 font-bold">
                ⚠ {invalidRows.length} with errors
              </span>
            )}
            {embeddedImageCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-amber-400 font-bold">
                <ImageIcon size={11} /> {embeddedImageCount} embedded image
                {embeddedImageCount > 1 ? "s" : ""} detected
              </span>
            )}
            <button
              onClick={() => {
                setStep("upload");
                setRows([]);
              }}
              className="ml-auto text-xs text-stone-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              ← Change file
            </button>
          </div>
          <div className="flex-1 overflow-auto px-6 py-4">
            <table className="w-full text-xs min-w-[1100px]">
              <thead>
                <tr className="bg-white/5 sticky top-0 z-10">
                  {[
                    "#",
                    "Image",
                    "Name *",
                    "SKU *",
                    "Category *",
                    "HSN",
                    "GST%",
                    "Buy ₹",
                    "Sell ₹ *",
                    "Stock",
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-3 py-2.5 text-stone-500 font-bold uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  const hasError = row._errors?.length > 0;
                  return (
                    <tr
                      key={ri}
                      className={`border-b border-white/5 ${hasError ? "bg-rose-500/5" : "hover:bg-white/3"}`}
                    >
                      <td className="px-3 py-2 text-stone-600 text-center text-[10px]">
                        {row._line}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col items-start gap-1">
                          {row.image_url ? (
                            <div className="relative group">
                              <img
                                src={row.image_url}
                                alt=""
                                className="w-14 h-14 object-cover rounded-lg border border-white/10"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                              {row._hasEmbeddedImage && (
                                <span className="absolute -top-1.5 -right-1.5 text-[8px] bg-emerald-500 text-white px-1 rounded-full font-bold leading-4">
                                  xlsx
                                </span>
                              )}
                              <button
                                onClick={() => editCell(ri, "image_url", "")}
                                className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} className="text-white" />
                              </button>
                            </div>
                          ) : (
                            <label className="w-14 h-14 rounded-lg border-2 border-dashed border-white/20 hover:border-amber-500/50 flex flex-col items-center justify-center cursor-pointer transition-colors group">
                              <ImageIcon
                                size={14}
                                className="text-stone-600 group-hover:text-amber-400 transition-colors"
                              />
                              <span className="text-[8px] text-stone-700 mt-0.5">
                                Upload
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                  handleRowImage(ri, e.target.files[0])
                                }
                              />
                            </label>
                          )}
                          {!row.image_url && (
                            <input
                              placeholder="or paste URL"
                              className="w-20 h-5 text-[9px] bg-white/5 border border-white/10 rounded px-1 text-stone-400 outline-none focus:border-amber-500/40"
                              onBlur={(e) => {
                                if (e.target.value.trim()) {
                                  editCell(
                                    ri,
                                    "image_url",
                                    e.target.value.trim(),
                                  );
                                  e.target.value = "";
                                }
                              }}
                            />
                          )}
                        </div>
                      </td>
                      {[
                        "name",
                        "sku",
                        "category",
                        "hsn",
                        "gst_rate",
                        "purchase_price",
                        "selling_price",
                        "stock_quantity",
                      ].map((col) => (
                        <td key={col} className="px-2 py-2">
                          <input
                            value={row[col] || ""}
                            onChange={(e) => editCell(ri, col, e.target.value)}
                            className={`w-full h-7 bg-white/5 border rounded px-2 text-white text-xs outline-none focus:ring-1 focus:ring-amber-500/40 ${hasError && ["name", "sku", "category", "selling_price"].includes(col) ? "border-rose-500/50" : "border-white/10"}`}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2">
                        <button
                          onClick={() => removeRow(ri)}
                          className="p-1 rounded hover:bg-rose-500/20 text-stone-600 hover:text-rose-400 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between flex-shrink-0">
            <div>
              {invalidRows.length > 0 && (
                <p className="text-xs text-amber-400 flex items-center gap-1.5">
                  <AlertCircle size={12} /> {invalidRows.length} row
                  {invalidRows.length > 1 ? "s" : ""} with errors will be
                  skipped.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="h-11 px-5 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!validRows.length}
                className="flex items-center gap-2 h-11 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm transition-all disabled:opacity-40"
              >
                <Upload size={15} /> Import {validRows.length} Product
                {validRows.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </>
      )}

      {step === "importing" && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 gap-6">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full border-4 border-amber-100/20" />
            <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-black text-sm">
                {progress.done}/{progress.total}
              </span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-white font-bold mb-1">Importing products…</p>
            <p className="text-stone-400 text-sm">
              Please wait, do not close this window
            </p>
          </div>
          <div className="w-full max-w-xs bg-white/10 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-amber-500 rounded-full"
              animate={{
                width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
              }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      )}
    </SpringModal>
  );
};

// ── Bulk Edit Modal ───────────────────────────────────────────
const BulkEditModal = ({ products, token, onClose, onSaved }) => {
  const [rows, setRows] = useState(() =>
    products.map((p) => ({
      id: p.id,
      name: p.name || "",
      category: p.category || "",
      hsn: p.hsn || "",
      discount: p.discount != null ? String(p.discount) : "0",
      discount_type: p.discount_type || "percentage",
      gst_rate: p.gst_rate != null ? String(p.gst_rate) : "",
      purchase_price: String(p.purchase_price ?? ""),
      selling_price: String(p.selling_price ?? ""),
      showroom_quantity: String(p.showroom_quantity ?? p.stock_quantity ?? ""),
      warehouse_quantity: String(p.warehouse_quantity ?? "0"),
      low_stock_threshold: String(p.low_stock_threshold ?? "10"),
      _original: {
        name: p.name || "",
        category: p.category || "",
        hsn: p.hsn || "",
        gst_rate: p.gst_rate != null ? String(p.gst_rate) : "",
        purchase_price: String(p.purchase_price ?? ""),
        selling_price: String(p.selling_price ?? ""),
        showroom_quantity: String(
          p.showroom_quantity ?? p.stock_quantity ?? "",
        ),
        warehouse_quantity: String(p.warehouse_quantity ?? "0"),
        low_stock_threshold: String(p.low_stock_threshold ?? "10"),
        discount: p.discount != null ? String(p.discount) : "0",
        discount_type: p.discount_type || "percentage",
        product_source: p.product_source || "showroom",
      },
    })),
  );
  const [saving, setSaving] = useState(false);
  const [filterText, setFilterText] = useState("");

  const isDirty = (row) => {
    const keys = [
      "name",
      "category",
      "hsn",
      "gst_rate",
      "purchase_price",
      "selling_price",
      "showroom_quantity",
      "warehouse_quantity",
      "low_stock_threshold",
      "discount",
      "discount_type",
      "product_source",
    ];
    return keys.some((k) => row[k] !== row._original[k]);
  };

  const setCell = (rowIdx, col, val) =>
    setRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [col]: val } : r)),
    );

  const dirtyRows = rows.filter(isDirty);
  const filteredRows = rows.filter(
    (r) =>
      !filterText ||
      r.name.toLowerCase().includes(filterText.toLowerCase()) ||
      r.category.toLowerCase().includes(filterText.toLowerCase()),
  );

  const handleSave = async () => {
    if (!dirtyRows.length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const payload = dirtyRows.map((row) => ({
        id: row.id,
        name: row.name.trim() || undefined,
        category: row.category.trim() || undefined,
        hsn: row.hsn.trim(),
        gst_rate: row.gst_rate !== "" ? parseFloat(row.gst_rate) : undefined,
        purchase_price:
          row.purchase_price !== ""
            ? parseFloat(row.purchase_price)
            : undefined,
        selling_price:
          row.selling_price !== "" ? parseFloat(row.selling_price) : undefined,
        showroom_quantity:
          row.showroom_quantity !== ""
            ? parseInt(row.showroom_quantity)
            : undefined,
        warehouse_quantity:
          row.warehouse_quantity !== ""
            ? parseInt(row.warehouse_quantity)
            : undefined,
        stock_quantity:
          (parseInt(row.showroom_quantity) || 0) +
          (parseInt(row.warehouse_quantity) || 0),
        low_stock_threshold:
          row.low_stock_threshold !== ""
            ? parseInt(row.low_stock_threshold)
            : undefined,
        discount: row.discount !== "" ? parseFloat(row.discount) : undefined,
        discount_type: row.discount_type,
      }));
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/bulk-update`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Saved ${res.data.updated ?? dirtyRows.length} product${dirtyRows.length !== 1 ? "s" : ""}`,
      );
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetRow = (rowIdx) =>
    setRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, ...r._original } : r)),
    );

  const COLS = [
    { key: "name", label: "Product name", w: "min-w-[180px]", type: "text" },
    { key: "category", label: "Category", w: "min-w-[120px]", type: "text" },
    {
      key: "hsn",
      label: "HSN code",
      w: "min-w-[110px]",
      type: "text",
      mono: true,
    },
    {
      key: "gst_rate",
      label: "GST %",
      w: "min-w-[80px]",
      type: "number",
      suffix: "%",
    },
    {
      key: "purchase_price",
      label: "Buy ₹",
      w: "min-w-[100px]",
      type: "number",
      prefix: "₹",
    },
    {
      key: "selling_price",
      label: "Sell ₹",
      w: "min-w-[100px]",
      type: "number",
      prefix: "₹",
    },
    {
      key: "showroom_quantity",
      label: "🏪 Showroom",
      w: "min-w-[90px]",
      type: "number",
    },
    {
      key: "warehouse_quantity",
      label: "🏭 Warehouse",
      w: "min-w-[90px]",
      type: "number",
    },
    {
      key: "low_stock_threshold",
      label: "Low alert",
      w: "min-w-[80px]",
      type: "number",
    },
    {
      key: "discount_type",
      label: "Disc Type",
      w: "min-w-[100px]",
      type: "select",
      options: [
        { value: "percentage", label: "%" },
        { value: "flat", label: "₹" },
      ],
    },
    { key: "discount", label: "Discount", w: "min-w-[90px]", type: "number" },
    {
      key: "product_source",
      label: "Source",
      w: "min-w-[110px]",
      type: "select",
      options: [
        { value: "showroom", label: "🏪 Showroom" },
        { value: "warehouse", label: "🏭 Warehouse" },
        { value: "both", label: "🔄 Both" },
      ],
    },
  ];

  return (
    <SpringModal onClose={onClose} maxW="max-w-7xl">
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/15 rounded-xl border border-violet-500/20">
            <TableProperties size={17} className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">
              Bulk Product Editor
            </h2>
            <p className="text-[10px] text-stone-500">
              Edit multiple products at once · {products.length} products loaded
              {dirtyRows.length > 0 && (
                <span className="ml-2 text-amber-400 font-bold">
                  · {dirtyRows.length} unsaved change
                  {dirtyRows.length > 1 ? "s" : ""}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden sm:block">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"
            />
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter rows…"
              className="h-9 w-44 bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 text-white text-xs outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="px-6 py-2 border-b border-white/5 flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
          <div className="w-3 h-3 rounded bg-amber-500/20 border border-amber-500/40 flex-shrink-0" />
          Changed row
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
          <div className="w-3 h-3 rounded bg-white/5 border border-white/10 flex-shrink-0" />
          Unchanged row
        </div>
        <p className="text-[11px] text-stone-600 ml-auto">
          Click any cell to edit · Tab to advance
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-[#1c1c2e]">
            <tr className="border-b border-white/10">
              <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-500 uppercase tracking-wider w-10">
                #
              </th>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-3 text-left text-[10px] font-bold text-stone-500 uppercase tracking-wider ${c.w}`}
                >
                  {c.label}
                </th>
              ))}
              <th className="px-3 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={COLS.length + 2}
                  className="text-center py-10 text-stone-500 text-sm"
                >
                  No products match your filter
                </td>
              </tr>
            )}
            {filteredRows.map((row, displayIdx) => {
              const actualIdx = rows.findIndex((r) => r.id === row.id);
              const dirty = isDirty(row);
              return (
                <tr
                  key={row.id}
                  className={`border-b transition-colors ${dirty ? "border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/8" : "border-white/5 hover:bg-white/3"}`}
                >
                  <td className="px-4 py-2 text-stone-600 text-center text-[10px] font-mono">
                    {displayIdx + 1}
                    {dirty && (
                      <span className="block w-1.5 h-1.5 rounded-full bg-amber-400 mx-auto mt-0.5" />
                    )}
                  </td>

                  {COLS.map((col) => (
                    <td key={col.key} className="px-2 py-1.5">
                      <div className="relative">
                        {col.prefix && (
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-400 text-[11px] font-bold pointer-events-none z-10">
                            {col.prefix}
                          </span>
                        )}
                        {col.type === "select" ? (
                          <select
                            value={row[col.key]}
                            onChange={(e) =>
                              setCell(actualIdx, col.key, e.target.value)
                            }
                            className={`w-full h-8 bg-white/5 border rounded-lg text-white text-xs outline-none focus:ring-2 focus:ring-amber-500/50 px-2
                              ${row[col.key] !== row._original[col.key] ? "border-amber-500/40 bg-amber-500/8" : "border-white/10"}
                            `}
                          >
                            {col.options.map((o) => (
                              <option
                                key={o.value}
                                value={o.value}
                                style={{ color: "black" }}
                              >
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={col.type}
                            value={row[col.key]}
                            onChange={(e) =>
                              setCell(actualIdx, col.key, e.target.value)
                            }
                            className={`w-full h-8 bg-white/5 border rounded-lg text-white text-xs outline-none focus:ring-2 focus:ring-amber-500/50 transition-all placeholder-stone-700
                              ${col.prefix ? "pl-6 pr-2" : col.suffix ? "pl-2 pr-5" : "px-2"}
                              ${row[col.key] !== row._original[col.key] ? "border-amber-500/40 bg-amber-500/8" : "border-white/10"}
                              ${col.mono ? "font-mono tracking-wider" : ""}
                            `}
                          />
                        )}
                        {col.suffix && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 text-[10px] pointer-events-none">
                            {col.suffix}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}

                  <td className="px-2 py-1.5">
                    {dirty && (
                      <button
                        onClick={() => resetRow(actualIdx)}
                        title="Reset this row"
                        className="p-1 rounded-lg text-stone-600 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                      >
                        <RefreshCw size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {dirtyRows.length > 0 ? (
            <p className="text-xs text-amber-400 flex items-center gap-1.5">
              <AlertCircle size={12} />
              {dirtyRows.length} row{dirtyRows.length > 1 ? "s" : ""} have
              unsaved changes
            </p>
          ) : (
            <p className="text-xs text-stone-500">
              No changes yet — edit cells above
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="h-11 px-5 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirtyRows.length}
            className="flex items-center gap-2 h-11 px-6 rounded-xl bg-violet-500 hover:bg-violet-400 text-white font-black text-sm transition-all disabled:opacity-40"
          >
            {saving ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Save size={13} />
            )}
            {saving
              ? "Saving…"
              : `Save ${dirtyRows.length || ""} Change${dirtyRows.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </SpringModal>
  );
};

// ── Bulk Print Quantity Modal ─────────────────────────────────
// Lets user choose exactly how many labels to print per selected product
const BulkPrintQtyModal = ({ products, selectedIds, token, onClose }) => {
  // initialise each selected product with qty = 1 (not full stock)
  const [qtys, setQtys] = useState(() => {
    const init = {};
    selectedIds.forEach((id) => {
      init[id] = 1;
    });
    return init;
  });
  const [printing, setPrinting] = useState(false);

  const selectedProducts = products.filter((p) => selectedIds.has(p.id));
  const totalLabels = selectedProducts.reduce(
    (s, p) => s + (qtys[p.id] || 0),
    0,
  );

  const setQty = (id, val) => {
    const n = Math.max(0, Math.min(9999, parseInt(val) || 0));
    setQtys((prev) => ({ ...prev, [id]: n }));
  };

  // Quick-fill helpers
  const setAll = (val) => {
    const next = {};
    selectedIds.forEach((id) => {
      next[id] = val;
    });
    setQtys(next);
  };
  const setAllToStock = () => {
    const next = {};
    selectedProducts.forEach((p) => {
      next[p.id] = p.stock_quantity || 0;
    });
    setQtys(next);
  };

  const handlePrint = async () => {
    if (totalLabels === 0) {
      toast.error("Set at least 1 label to print");
      return;
    }
    setPrinting(true);
    try {
      const labels = selectedProducts.flatMap((p) =>
        Array.from({ length: qtys[p.id] || 0 }, () => ({
          name: p.name,
          sku: p.barcode || p.sku,
          price: p.selling_price,
        })),
      );
      await printLabels(labels, token);
      if (getPrintMode() === "thermal")
        toast.success(
          `${labels.length} label${labels.length > 1 ? "s" : ""} sent to printer!`,
        );
      onClose();
    } catch (err) {
      toast.error(err.message || "Print failed");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <SpringModal onClose={onClose} maxW="max-w-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl border border-amber-500/20">
            <Printer size={17} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">Print Labels</h2>
            <p className="text-[10px] text-stone-500">
              Set how many labels to print per product
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Quick-fill bar */}
      <div className="px-6 py-3 border-b border-white/5 flex items-center gap-2 flex-shrink-0">
        <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mr-1">
          Quick set:
        </span>
        {[1, 2, 5, 10].map((n) => (
          <button
            key={n}
            onClick={() => setAll(n)}
            className="px-2.5 py-1 rounded-lg bg-white/8 hover:bg-amber-500/20 hover:text-amber-400 text-stone-400 text-xs font-bold border border-white/10 transition-all"
          >
            {n}
          </button>
        ))}
        <button
          onClick={setAllToStock}
          className="px-2.5 py-1 rounded-lg bg-white/8 hover:bg-emerald-500/20 hover:text-emerald-400 text-stone-400 text-xs font-bold border border-white/10 transition-all ml-1"
        >
          All stock
        </button>
        <button
          onClick={() => setAll(0)}
          className="px-2.5 py-1 rounded-lg bg-white/8 hover:bg-rose-500/20 hover:text-rose-400 text-stone-400 text-xs font-bold border border-white/10 transition-all"
        >
          Reset
        </button>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {selectedProducts.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-3 py-2.5"
          >
            {/* Product image */}
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`}
              alt={p.name}
              className="w-10 h-10 object-cover rounded-lg border border-white/10 flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {/* Name + meta */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold truncate">{p.name}</p>
              <p className="text-stone-500 text-[10px] font-mono">
                {p.sku || p.barcode}
              </p>
              <p className="text-stone-600 text-[10px]">
                Stock:{" "}
                <span className="text-amber-400 font-bold">
                  {p.stock_quantity}
                </span>
              </p>
            </div>
            {/* Qty stepper */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setQty(p.id, (qtys[p.id] || 0) - 1)}
                className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <MinusIcon size={11} />
              </button>
              <input
                type="number"
                min="0"
                max="9999"
                value={qtys[p.id] ?? 1}
                onChange={(e) => setQty(p.id, e.target.value)}
                className="w-14 h-7 bg-white/5 border border-white/10 rounded-lg text-center text-white text-xs font-black outline-none focus:ring-2 focus:ring-amber-500/40"
              />
              <button
                onClick={() => setQty(p.id, (qtys[p.id] || 0) + 1)}
                className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <Plus size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Barcode size={14} className="text-amber-400" />
          <span className="text-sm text-white font-black">
            {totalLabels} label{totalLabels !== 1 ? "s" : ""} total
          </span>
          <span className="text-[10px] text-stone-500">
            across {selectedProducts.length} product
            {selectedProducts.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="h-11 px-4 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={printing || totalLabels === 0}
            className="flex items-center gap-2 h-11 px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm transition-all disabled:opacity-50"
          >
            {printing ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Printer size={13} />
            )}
            {printing
              ? "Printing…"
              : `Print ${totalLabels} Label${totalLabels !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </SpringModal>
  );
};

// ── Main Inventory Page ───────────────────────────────────────
const Inventory = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "manager"].includes(user?.role);
  const isReadOnly = user?.role === "staff";

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const [products, setProducts] = useState([]);
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [rackProduct, setRackProduct] = useState(null);
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [printMode, setPrintModeState] = useState(getPrintMode);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  // ── NEW: controls the print-qty picker modal ──
  const [showBulkPrintQty, setShowBulkPrintQty] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const handlePrintModeChange = (mode) => {
    setPrintMode(mode);
    setPrintModeState(mode);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes] = await Promise.all([
        axios.get(`${API_URL}/products`, {
          headers,
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: searchDebounced,
          },
        }),
        axios.get(`${API_URL}/racks`, { headers }),
      ]);
      setProducts(prodRes.data.products);
      setTotalProducts(prodRes.data.pagination.total);
      setTotalPages(prodRes.data.pagination.pages);
    } catch {
      toast.error("Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  }, [headers, currentPage, itemsPerPage, searchDebounced]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const stats = useMemo(
    () => ({
      lowStock: products.filter(
        (p) => p.stock_quantity <= p.low_stock_threshold,
      ).length,
      categories: new Set(products.map((p) => p.category)).size,
      totalValue: products.reduce(
        (s, p) => s + p.selling_price * p.stock_quantity,
        0,
      ),
    }),
    [products],
  );

  const handleSave = useCallback(
    async (data) => {
      try {
        if (editingProduct) {
          await axios.put(`${API_URL}/products/${editingProduct.id}`, data, {
            headers,
          });
          toast.success("Product updated");
        } else {
          await axios.post(`${API_URL}/products`, data, { headers });
          toast.success("Product added");
        }
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to save");
        throw err;
      }
    },
    [editingProduct, headers, fetchAll],
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this product?")) return;
      try {
        await axios.delete(`${API_URL}/products/${id}`, { headers });
        toast.success("Product deleted");
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to delete");
      }
    },
    [headers, fetchAll],
  );

  const toggleBulkSelect = useCallback((id) => {
    setBulkSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  // Legacy "print all stock" fast-path — kept for the "Select All" → direct print shortcut
  const handleBulkPrint = useCallback(async () => {
    if (!bulkSelected.size) return;
    setBulkPrinting(true);
    try {
      const labels = products
        .filter((p) => bulkSelected.has(p.id))
        .flatMap((p) =>
          Array.from({ length: p.stock_quantity || 1 }, () => ({
            name: p.name,
            sku: p.barcode || p.sku,
            price: p.selling_price,
          })),
        );
      await printLabels(labels, token);
      if (getPrintMode() === "thermal")
        toast.success(
          `${labels.length} label${labels.length > 1 ? "s" : ""} sent to printer!`,
        );
      setBulkSelected(new Set());
    } catch (err) {
      toast.error(err.message || "Failed to print labels");
    } finally {
      setBulkPrinting(false);
    }
  }, [bulkSelected, products, token]);

  const openEditModal = useCallback((product) => {
    setEditingProduct(product);
    setShowProductModal(true);
  }, []);
  const closeProductModal = useCallback(() => {
    setShowProductModal(false);
    setEditingProduct(null);
  }, []);

  const displayedProducts = products;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const totalLabels = products
    .filter((p) => bulkSelected.has(p.id))
    .reduce((s, p) => s + (p.stock_quantity || 1), 0);

  return (
    <div
      className="min-h-full bg-[#f7f6f3] p-4 md:p-6 lg:p-8"
      data-testid="inventory-page"
    >
      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-[#1c1c2e] rounded-2xl p-6 mb-6 shadow-xl"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 40%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 15% 80%, #6366f1 0%, transparent 40%)",
          }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/15 rounded-2xl border border-amber-500/20">
              <Package size={24} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                {t("inventory.title")}
              </h1>
              <p className="text-stone-400 text-sm mt-0.5">
                {t("inventory.subtitle")}
              </p>
            </div>
          </div>
          <div className="smallfit flex items-center gap-2 flex-shrink-0 flex-wrap">
            <button
              onClick={() => setShowCSVModal(true)}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-stone-300 hover:text-white font-bold text-sm px-4 py-3 rounded-xl transition-all whitespace-nowrap"
            >
              <Upload size={15} /> Import CSV
            </button>
            {canEdit && (
              <button
                onClick={() => setShowBulkEdit(true)}
                className="flex items-center gap-2 bg-violet-600/80 hover:bg-violet-500 border border-violet-500/30 text-white font-bold text-sm px-4 py-3 rounded-xl transition-all whitespace-nowrap"
              >
                <TableProperties size={15} /> Bulk Edit
              </button>
            )}
            <button
              onClick={() =>
                setBulkSelected(new Set(displayedProducts.map((p) => p.id)))
              }
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-stone-300 hover:text-white font-bold text-sm px-4 py-3 rounded-xl transition-all whitespace-nowrap"
            >
              <Barcode size={15} /> Print Labels
            </button>
            {canEdit && (
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setShowProductModal(true);
                }}
                data-testid="add-product-button"
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm px-5 py-3 rounded-xl transition-all shadow-lg shadow-amber-900/30 active:scale-95 whitespace-nowrap"
              >
                <Plus size={16} /> {t("inventory.addProduct")}
              </button>
            )}
          </div>
        </div>
        <div className="relative mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Total Products",
              value: totalProducts,
              color: "text-stone-300",
            },
            {
              label: "Categories",
              value: stats.categories,
              color: "text-blue-400",
            },
            {
              label: "Low Stock",
              value: stats.lowStock,
              color: stats.lowStock > 0 ? "text-amber-400" : "text-stone-500",
            },
            {
              label: "Inventory Value",
              value: `₹${stats.totalValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
              color: "text-emerald-400",
            },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                {s.label}
              </p>
              <p className={`font-mono font-black text-lg ${s.color}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative mb-5"
      >
        <Search
          size={15}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
        />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("inventory.searchPlaceholder")}
          data-testid="search-input"
          className="w-full h-11 bg-white border border-stone-200 rounded-xl pl-10 pr-4 text-sm text-stone-700 outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400/50 shadow-sm"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 p-1"
          >
            <X size={13} />
          </button>
        )}
      </motion.div>

      {/* Bulk print bar */}
      <AnimatePresence>
        {bulkSelected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-[#1c1c2e] border border-amber-500/30 rounded-2xl px-5 py-3.5 mb-4 flex items-center justify-between shadow-xl"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/15 rounded-xl">
                <Barcode size={15} className="text-amber-400" />
              </div>
              <div>
                <p className="text-white font-black text-sm">
                  {bulkSelected.size} product{bulkSelected.size > 1 ? "s" : ""}{" "}
                  selected
                </p>
                <p className="text-stone-500 text-[10px]">
                  Ready to print barcode labels
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setBulkSelected(new Set())}
                className="h-8 px-3 rounded-lg border border-white/10 text-stone-400 hover:text-white text-xs font-semibold transition-all"
              >
                Clear
              </button>
              <button
                onClick={() =>
                  setBulkSelected(new Set(displayedProducts.map((p) => p.id)))
                }
                className="h-8 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-stone-300 hover:text-white text-xs font-semibold transition-all"
              >
                Select All (
                {displayedProducts.reduce(
                  (s, p) => s + (p.stock_quantity || 1),
                  0,
                )}{" "}
                labels)
              </button>
              {/* ── NEW: Choose Qty button — opens the qty picker modal ── */}
              <button
                onClick={() => setShowBulkPrintQty(true)}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white/10 hover:bg-amber-500/20 hover:text-amber-400 text-stone-300 text-xs font-semibold border border-white/10 hover:border-amber-500/30 transition-all"
              >
                <Pencil size={11} /> Choose Qty
              </button>
              {/* Print all stock fast path */}
              <button
                onClick={handleBulkPrint}
                disabled={bulkPrinting}
                className="flex items-center gap-2 h-8 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-xs transition-all disabled:opacity-50"
              >
                <Printer size={12} />
                Print All Stock ({totalLabels})
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="relative w-14 h-14 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-amber-100" />
              <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
            </div>
            <p className="text-stone-400 text-sm">{t("inventory.loading")}</p>
          </div>
        </div>
      ) : displayedProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-stone-100 shadow-sm">
          <div className="p-4 bg-stone-100 rounded-2xl mb-4">
            <Package size={32} className="text-stone-300" />
          </div>
          <p className="text-stone-500 font-semibold">
            {t("inventory.noProducts")}
          </p>
          <p className="text-stone-400 text-sm mt-1">
            {totalProducts > 0
              ? "Try adjusting your search"
              : "Add your first product to get started"}
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-2xl border border-stone-100 overflow-hidden shadow-sm">
            <table className="w-full" data-testid="products-table">
              <thead>
                <tr className="bg-[#1c1c2e]">
                  {[
                    "Product Name",
                    "SKU / Barcode",
                    "Category",
                    "HSN · GST",
                    "Price",
                    "Stock",
                    "discount",
                    "Actions",
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-3.5 text-xs font-bold text-stone-400 uppercase tracking-wider ${i === 6 ? "text-right" : "text-left"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedProducts.map((product, i) => {
                  const isLow =
                    product.stock_quantity <= product.low_stock_threshold;
                  const profit = product.selling_price - product.purchase_price;
                  return (
                    <motion.tr
                      key={product.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className={`border-t border-stone-100 hover:bg-amber-50/30 transition-colors ${i % 2 !== 0 ? "bg-stone-50/40" : ""}`}
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div
                          className="flex items-center gap-3 cursor-pointer group/name"
                          onClick={() => setViewProduct(product)}
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`}
                            alt={product.name}
                            className="w-11 h-11 object-cover rounded-xl border border-stone-100 flex-shrink-0 group-hover/name:ring-2 group-hover/name:ring-amber-400 transition-all"
                          />
                          <div>
                            <p className="font-bold text-stone-800 text-sm leading-tight group-hover/name:text-amber-600 transition-colors">
                              {product.name}
                            </p>
                            {product.supplier && (
                              <p className="text-[10px] text-stone-400 mt-0.5">
                                {product.supplier}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-xs font-mono font-bold text-stone-700">
                          {product.sku}
                        </p>
                        {product.barcode && (
                          <p className="text-[10px] font-mono text-stone-400">
                            {product.barcode}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-[11px] font-bold bg-stone-100 text-stone-600 px-2.5 py-1 rounded-full">
                          {product.category}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            product.product_source === "warehouse"
                              ? "bg-blue-50 text-blue-600 border border-blue-200"
                              : product.product_source === "both"
                                ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {product.product_source === "warehouse"
                            ? "🏭 Warehouse"
                            : product.product_source === "both"
                              ? "🔄 Both"
                              : "🏪 Showroom"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {product.hsn ? (
                          <p className="text-xs font-mono text-stone-700 font-semibold">
                            {product.hsn}
                          </p>
                        ) : (
                          <p className="text-[10px] text-stone-300 italic">
                            No HSN
                          </p>
                        )}
                        {product.gst_rate != null ? (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full mt-0.5 inline-block">
                            GST {product.gst_rate}%
                          </span>
                        ) : (
                          <span className="text-[10px] text-stone-300 italic block mt-0.5">
                            No GST
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="font-mono font-black text-stone-800">
                          ₹{product.selling_price.toFixed(2)}
                        </p>
                        <p className="text-[10px] text-stone-400">
                          {t("inventory.cost")}: ₹{product.purchase_price} ·{" "}
                          <span className="text-emerald-600">
                            +₹{profit.toFixed(0)}
                          </span>
                        </p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full ${product.stock_quantity === 0 ? "bg-rose-50 text-rose-600 border border-rose-200" : isLow ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                        >
                          {isLow && product.stock_quantity > 0 && (
                            <AlertTriangle size={10} />
                          )}
                          {product.stock_quantity} {t("inventory.units")}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-xs font-mono font-bold text-stone-700">
                          {product.discount}
                        </p>
                        <p className="text-[10px] font-mono text-stone-400">
                          {product.discount_type}
                        </p>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleBulkSelect(product.id)}
                            className={`p-2 rounded-xl transition-colors ${bulkSelected.has(product.id) ? "bg-amber-500/20 text-amber-500" : "hover:bg-stone-100 text-stone-300 hover:text-stone-500"}`}
                          >
                            {bulkSelected.has(product.id) ? (
                              <CheckCheck size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </button>
                          <button
                            onClick={() => setBarcodeProduct(product)}
                            className="p-2 rounded-xl hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition-colors"
                            title="View barcode"
                          >
                            <Barcode size={14} />
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => openEditModal(product)}
                              className="p-2 rounded-xl hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition-colors"
                              data-testid={`edit-button-${product.id}`}
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => setRackProduct(product)}
                              className="p-2 rounded-xl hover:bg-blue-50 text-stone-400 hover:text-blue-600 transition-colors"
                              data-testid={`racks-button-${product.id}`}
                            >
                              <MapPin size={14} />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 rounded-xl hover:bg-rose-50 text-stone-400 hover:text-rose-600 transition-colors"
                              data-testid={`delete-button-${product.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                          {isReadOnly && (
                            <span className="text-[10px] text-stone-300 italic px-2">
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {displayedProducts.map((product, i) => {
              const isLow =
                product.stock_quantity <= product.low_stock_threshold;
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white border border-stone-100 rounded-2xl p-4 shadow-sm cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => setViewProduct(product)}
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="flex gap-3">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-xl flex-shrink-0 border border-stone-100"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-bold text-stone-800 text-sm leading-tight truncate">
                          {product.name}
                        </p>
                        <span
                          className={`text-[10px] font-black px-2 py-1 rounded-full flex-shrink-0 ${isLow ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}
                        >
                          {product.stock_quantity}
                        </span>
                      </div>
                      <p className="text-[10px] text-stone-400 mb-1">
                        {product.category} · {product.sku}
                      </p>
                      {(product.hsn || product.gst_rate != null) && (
                        <p className="text-[10px] text-stone-400 mb-1">
                          {product.hsn && (
                            <span className="font-mono">{product.hsn}</span>
                          )}
                          {product.hsn && product.gst_rate != null && " · "}
                          {product.gst_rate != null && (
                            <span className="text-emerald-600 font-bold">
                              GST {product.gst_rate}%
                            </span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-black text-stone-800">
                          ₹{product.selling_price}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBulkSelect(product.id);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${bulkSelected.has(product.id) ? "bg-amber-500/20 text-amber-500" : "bg-stone-50 text-stone-300 hover:text-stone-500"}`}
                          >
                            <Check size={13} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBarcodeProduct(product);
                            }}
                            className="p-1.5 rounded-lg bg-stone-50 text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <Barcode size={13} />
                          </button>
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(product);
                              }}
                              className="p-1.5 rounded-lg bg-stone-50 text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            >
                              <Pencil size={13} />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRackProduct(product);
                              }}
                              className="p-1.5 rounded-lg bg-stone-50 text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <MapPin size={13} />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(product.id);
                              }}
                              className="p-1.5 rounded-lg bg-stone-50 text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-stone-200">
              <div className="flex items-center gap-3">
                <span className="text-xs text-stone-500">
                  Showing {startIndex + 1} –{" "}
                  {Math.min(currentPage * itemsPerPage, totalProducts)} of{" "}
                  {totalProducts} products
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 px-2 rounded-lg border border-stone-200 bg-white text-stone-700 text-xs font-medium outline-none focus:ring-2 focus:ring-amber-500/30"
                >
                  {[5, 10, 20, 48, 96, 240, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="flex items-center gap-1 px-2">
                  {(() => {
                    const maxVisible = 5;
                    let startPage = Math.max(
                      1,
                      currentPage - Math.floor(maxVisible / 2),
                    );
                    let endPage = Math.min(
                      totalPages,
                      startPage + maxVisible - 1,
                    );
                    if (endPage - startPage + 1 < maxVisible)
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    return Array.from(
                      { length: endPage - startPage + 1 },
                      (_, i) => startPage + i,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-all ${currentPage === page ? "bg-amber-500 text-white shadow-sm" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-amber-600"}`}
                      >
                        {page}
                      </button>
                    ));
                  })()}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showProductModal && (
          <ProductModal
            editingProduct={editingProduct}
            token={token}
            onClose={closeProductModal}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {rackProduct && (
          <RackModal
            product={rackProduct}
            racks={racks}
            token={token}
            onClose={() => setRackProduct(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {barcodeProduct && (
          <BarcodeModal
            product={barcodeProduct}
            token={token}
            onClose={() => setBarcodeProduct(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCSVModal && (
          <XLSXImportModal
            token={token}
            onClose={() => setShowCSVModal(false)}
            onImport={fetchAll}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showBulkEdit && products.length > 0 && (
          <BulkEditModal
            products={products}
            token={token}
            onClose={() => setShowBulkEdit(false)}
            onSaved={fetchAll}
          />
        )}
      </AnimatePresence>
      {/* ── Bulk Print Qty Modal ── */}
      <AnimatePresence>
        {showBulkPrintQty && bulkSelected.size > 0 && (
          <BulkPrintQtyModal
            products={products}
            selectedIds={bulkSelected}
            token={token}
            onClose={() => setShowBulkPrintQty(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Inventory;
